/**
 * SubredditStatsTable.tsx - Per-subreddit crawler statistics table
 *
 * SESSION_400: Displays detailed breakdown of crawler performance by subreddit
 *
 * Design: Industrial data table with monospace numerics, priority tier badges,
 * and subtle row highlighting for emphasis.
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Table,
  Paper,
  Group,
  Stack,
  Text,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  Skeleton,
  UnstyledButton
} from '@mantine/core';
import {
  IconBrandReddit,
  IconSortAscending,
  IconSortDescending,
  IconArrowsSort,
  IconClock,
  IconExternalLink
} from '@tabler/icons-react';
import type { SubredditCrawlStats } from '../../../../domain/entities/Region';

// ============================================================================
// Types
// ============================================================================

interface SubredditStatsTableProps {
  stats: SubredditCrawlStats[];
  isLoading: boolean;
}

type SortField = 'subredditName' | 'crawlCount' | 'postsProcessed' | 'mentionsFound' | 'pendingMatchCount' | 'averageDurationSeconds' | 'lastCrawlAt';
type SortDirection = 'asc' | 'desc';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format seconds to human-readable duration
 */
function formatDuration(seconds: number): string {
  if (seconds === 0) return '—';
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/**
 * Format relative time from ISO date string
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    return `${hours}h ago`;
  }
  const days = Math.floor(diffMins / 1440);
  return `${days}d ago`;
}

/**
 * Get priority tier badge color
 */
function getPriorityColor(tier: string): string {
  switch (tier.toLowerCase()) {
    case 'high':
      return 'teal';
    case 'medium':
      return 'blue';
    case 'low':
      return 'gray';
    case 'verylow':
      return 'dark';
    default:
      return 'gray';
  }
}

/**
 * Format numbers with locale formatting
 */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

// ============================================================================
// Sort Header Component
// ============================================================================

interface SortableHeaderProps {
  field: SortField;
  label: string;
  currentSort: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  label,
  currentSort,
  direction,
  onSort,
  align = 'left'
}): React.ReactElement => {
  const isActive = currentSort === field;

  const SortIcon = useMemo(() => {
    if (!isActive) return IconArrowsSort;
    return direction === 'asc' ? IconSortAscending : IconSortDescending;
  }, [isActive, direction]);

  return (
    <UnstyledButton
      onClick={(): void => onSort(field)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        width: '100%',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        padding: '4px 0',
        borderRadius: 4,
        transition: 'color 0.15s ease'
      }}
    >
      <Text
        size="xs"
        fw={600}
        tt="uppercase"
        c={isActive ? 'blue.6' : 'dimmed'}
        style={{ letterSpacing: '0.03em' }}
      >
        {label}
      </Text>
      <SortIcon
        size={14}
        style={{
          color: isActive
            ? 'var(--mantine-color-blue-6)'
            : 'var(--mantine-color-gray-5)',
          transition: 'color 0.15s ease'
        }}
      />
    </UnstyledButton>
  );
};

// ============================================================================
// Table Row Component
// ============================================================================

interface TableRowProps {
  stat: SubredditCrawlStats;
  index: number;
}

const TableRow: React.FC<TableRowProps> = ({ stat, index }): React.ReactElement => {
  const priorityColor = getPriorityColor(stat.priorityTier);
  const isRecent = stat.lastCrawlAt && (Date.now() - new Date(stat.lastCrawlAt).getTime()) < 3600000; // Within 1 hour

  return (
    <Table.Tr
      style={{
        animationDelay: `${index * 30}ms`,
        backgroundColor: isRecent ? 'var(--mantine-color-teal-0)' : undefined,
        transition: 'background-color 0.2s ease'
      }}
    >
      {/* Subreddit Name */}
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <IconBrandReddit
            size={18}
            color="var(--mantine-color-orange-6)"
            style={{ flexShrink: 0 }}
          />
          <Stack gap={0}>
            <Group gap={4} wrap="nowrap">
              <Text
                size="sm"
                fw={600}
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                r/{stat.subredditName}
              </Text>
              <Tooltip label="Open in Reddit" position="right" withArrow>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  component="a"
                  href={`https://reddit.com/r/${stat.subredditName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ opacity: 0.5, transition: 'opacity 0.15s ease' }}
                  styles={{
                    root: {
                      '&:hover': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  <IconExternalLink size={12} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Group>
      </Table.Td>

      {/* Priority Tier */}
      <Table.Td>
        <Badge
          size="sm"
          variant="light"
          color={priorityColor}
          style={{
            fontWeight: 600,
            textTransform: 'capitalize'
          }}
        >
          {stat.priorityTier}
        </Badge>
      </Table.Td>

      {/* Crawl Count */}
      <Table.Td ta="right">
        <Text
          size="sm"
          fw={500}
          style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
        >
          {formatNumber(stat.crawlCount)}
        </Text>
      </Table.Td>

      {/* Posts/Comments */}
      <Table.Td ta="right">
        <Stack gap={0}>
          <Text
            size="sm"
            fw={500}
            style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
          >
            {formatNumber(stat.postsProcessed)} posts
          </Text>
          <Text size="xs" c="dimmed">
            +{formatNumber(stat.commentsProcessed)} comments
          </Text>
        </Stack>
      </Table.Td>

      {/* Mentions Found */}
      <Table.Td ta="right">
        <Text
          size="sm"
          fw={600}
          c={stat.mentionsFound > 0 ? 'teal.7' : 'dimmed'}
          style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
        >
          {formatNumber(stat.mentionsFound)}
        </Text>
      </Table.Td>

      {/* Pending Match Count - SESSION_403 */}
      <Table.Td ta="right">
        <Text
          size="sm"
          fw={stat.pendingMatchCount > 100 ? 600 : 500}
          c={stat.pendingMatchCount > 100 ? 'orange.7' : stat.pendingMatchCount > 0 ? 'yellow.7' : 'dimmed'}
          style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
        >
          {formatNumber(stat.pendingMatchCount)}
        </Text>
      </Table.Td>

      {/* Average Duration */}
      <Table.Td ta="right">
        <Text
          size="sm"
          fw={500}
          c="dimmed"
          style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
        >
          {formatDuration(stat.averageDurationSeconds)}
        </Text>
      </Table.Td>

      {/* Last Crawl */}
      <Table.Td ta="right">
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {isRecent && (
            <Box
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-teal-6)',
                flexShrink: 0
              }}
            />
          )}
          <Tooltip
            label={stat.lastCrawlAt ? new Date(stat.lastCrawlAt).toLocaleString() : 'Never crawled'}
            position="left"
            withArrow
          >
            <Text
              size="sm"
              c={isRecent ? 'teal.7' : 'dimmed'}
              style={{ cursor: 'help' }}
            >
              {formatRelativeTime(stat.lastCrawlAt)}
            </Text>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const SubredditStatsTable: React.FC<SubredditStatsTableProps> = ({
  stats,
  isLoading
}): React.ReactElement => {
  const [sortField, setSortField] = useState<SortField>('mentionsFound');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle sort toggle
  const handleSort = useCallback((field: SortField): void => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  // Sort data
  const sortedStats = useMemo((): SubredditCrawlStats[] => {
    if (!stats || stats.length === 0) return [];

    return [...stats].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'subredditName':
          comparison = a.subredditName.localeCompare(b.subredditName);
          break;
        case 'crawlCount':
          comparison = a.crawlCount - b.crawlCount;
          break;
        case 'postsProcessed':
          comparison = a.postsProcessed - b.postsProcessed;
          break;
        case 'mentionsFound':
          comparison = a.mentionsFound - b.mentionsFound;
          break;
        case 'pendingMatchCount':
          comparison = a.pendingMatchCount - b.pendingMatchCount;
          break;
        case 'averageDurationSeconds':
          comparison = a.averageDurationSeconds - b.averageDurationSeconds;
          break;
        case 'lastCrawlAt':
          const aTime = a.lastCrawlAt ? new Date(a.lastCrawlAt).getTime() : 0;
          const bTime = b.lastCrawlAt ? new Date(b.lastCrawlAt).getTime() : 0;
          comparison = aTime - bTime;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [stats, sortField, sortDirection]);

  // Loading state
  if (isLoading) {
    return (
      <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
        <Stack gap={0}>
          <Skeleton height={44} radius={0} />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={60} radius={0} mt={1} />
          ))}
        </Stack>
      </Paper>
    );
  }

  // Empty state
  if (!stats || stats.length === 0) {
    return (
      <Paper
        p="xl"
        radius="md"
        withBorder
        style={{
          textAlign: 'center',
          borderStyle: 'dashed',
          borderColor: 'var(--mantine-color-gray-4)'
        }}
      >
        <Stack align="center" gap="md">
          <IconBrandReddit size={40} color="var(--mantine-color-gray-4)" />
          <Stack gap={4}>
            <Text fw={600} c="dimmed">No subreddit data</Text>
            <Text size="sm" c="dimmed">
              No crawl statistics available for the selected time period.
            </Text>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      radius="md"
      withBorder
      style={{
        overflow: 'hidden',
        borderColor: 'var(--mantine-color-gray-3)'
      }}
    >
      {/* Table Header */}
      <Box
        px="md"
        py="sm"
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderBottom: '1px solid var(--mantine-color-gray-3)'
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconBrandReddit size={18} color="var(--mantine-color-orange-6)" />
            <Text fw={600} size="sm">Subreddit Performance</Text>
          </Group>
          <Text size="xs" c="dimmed">
            {stats.length} subreddit{stats.length !== 1 ? 's' : ''}
          </Text>
        </Group>
      </Box>

      {/* Table */}
      <Table.ScrollContainer minWidth={800}>
        <Table
          highlightOnHover
          horizontalSpacing="md"
          verticalSpacing="sm"
          styles={{
            th: {
              backgroundColor: 'var(--mantine-color-gray-0)',
              borderBottom: '2px solid var(--mantine-color-gray-3)'
            },
            tr: {
              borderBottom: '1px solid var(--mantine-color-gray-2)'
            }
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 200 }}>
                <SortableHeader
                  field="subredditName"
                  label="Subreddit"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
              </Table.Th>
              <Table.Th style={{ width: 100 }}>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.03em' }}>
                  Priority
                </Text>
              </Table.Th>
              <Table.Th style={{ width: 100, textAlign: 'right' }}>
                <SortableHeader
                  field="crawlCount"
                  label="Crawls"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
              <Table.Th style={{ width: 140, textAlign: 'right' }}>
                <SortableHeader
                  field="postsProcessed"
                  label="Content"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
              <Table.Th style={{ width: 100, textAlign: 'right' }}>
                <SortableHeader
                  field="mentionsFound"
                  label="Mentions"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
              <Table.Th style={{ width: 100, textAlign: 'right' }}>
                <SortableHeader
                  field="pendingMatchCount"
                  label="Pending"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
              <Table.Th style={{ width: 100, textAlign: 'right' }}>
                <SortableHeader
                  field="averageDurationSeconds"
                  label="Avg Time"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
              <Table.Th style={{ width: 120, textAlign: 'right' }}>
                <SortableHeader
                  field="lastCrawlAt"
                  label="Last Crawl"
                  currentSort={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedStats.map((stat, index) => (
              <TableRow
                key={stat.redditSourceId}
                stat={stat}
                index={index}
              />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* Table Footer with Summary */}
      <Box
        px="md"
        py="sm"
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderTop: '1px solid var(--mantine-color-gray-3)'
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="xl">
            <Group gap={4}>
              <Text size="xs" c="dimmed">Total Crawls:</Text>
              <Text
                size="xs"
                fw={600}
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                {formatNumber(stats.reduce((sum, s) => sum + s.crawlCount, 0))}
              </Text>
            </Group>
            <Group gap={4}>
              <Text size="xs" c="dimmed">Total Mentions:</Text>
              <Text
                size="xs"
                fw={600}
                c="teal.7"
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                {formatNumber(stats.reduce((sum, s) => sum + s.mentionsFound, 0))}
              </Text>
            </Group>
            <Group gap={4}>
              <Text size="xs" c="dimmed">Total Pending:</Text>
              <Text
                size="xs"
                fw={600}
                c="orange.7"
                style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}
              >
                {formatNumber(stats.reduce((sum, s) => sum + s.pendingMatchCount, 0))}
              </Text>
            </Group>
          </Group>
          <Group gap={4}>
            <IconClock size={12} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed">
              Sorted by {sortField.replace(/([A-Z])/g, ' $1').toLowerCase()}
            </Text>
          </Group>
        </Group>
      </Box>
    </Paper>
  );
};

export default SubredditStatsTable;
